/**
 * frontend/src/pages/valuation/_useIntakeInputs.ts
 *
 * VAL-INPUT-PERSIST-1A — split out of ValuationIntake.tsx under
 * FILE_SIZE_POLICY.md ahead of pieces B and D, which both add fields here.
 * No behaviour change: the 16 input `useState`s, their hydration from the
 * working draft (`hydrateFields`, the hydration effect, `hydrated`) and
 * `startClean()` moved verbatim, plus `intakeDraftPatch` — the exact
 * `SaveDraftBody` ValuationIntake.tsx built inline for the pre-run save.
 */
import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react'
import type { HouseDefault, RunBody } from '@/types/valuation'
import type { DraftInputs, OverrideDisposition, SaveDraftBody } from '@/types/valuationDraft'
import { INTAKE_DEFAULTS, num, type HydrateKey } from './_valuationIntakeHelpers'

/**
 * VAL-DRAFT-OVERWRITE-1 (Intake) — `useState` whose setter records that the
 * user has edited the field. A late draft hydrate fills only fields not in
 * `touched`; `raw` is the screen's own write (hydrate, start clean) and does
 * not count as an edit.
 */
function useField(touched: Set<string>, name: string, init: string) {
  const [value, raw] = useState(init)
  const set = useCallback((v: string) => {
    touched.add(name)
    raw(v)
  }, [touched, name])
  return [value, set, raw] as const
}

export function useIntakeInputs(
  draft: DraftInputs | null,
  draftLoading: boolean,
  dismissDraft: () => void,
  // FIX_VAL-OWNER-INPUTS-2 — server-served house starting values (owner only; null for the signer).
  starting: Record<string, HouseDefault> | null = null,
) {
  // Names of fields the user has typed in (VAL-DRAFT-OVERWRITE-1).
  const touched = useRef(new Set<string>())
  // Income leg
  const [riskFree, setRiskFree, setRiskFreeRaw] = useField(touched.current, 'riskFree', INTAKE_DEFAULTS.riskFree)
  const [equityRiskPremium, setEquityRiskPremium, setEquityRiskPremiumRaw] = useField(touched.current, 'equityRiskPremium', INTAKE_DEFAULTS.equityRiskPremium)
  const [sizePremium, setSizePremium, setSizePremiumRaw] = useField(touched.current, 'sizePremium', INTAKE_DEFAULTS.sizePremium)
  const [companySpecificRisk, setCompanySpecificRisk, setCompanySpecificRiskRaw] = useField(touched.current, 'companySpecificRisk', INTAKE_DEFAULTS.companySpecificRisk)
  const [longTermGrowth, setLongTermGrowth, setLongTermGrowthRaw] = useField(touched.current, 'longTermGrowth', INTAKE_DEFAULTS.longTermGrowth)
  const [normalizedEarnings, setNormalizedEarnings, setNormalizedEarningsRaw] = useField(touched.current, 'normalizedEarnings', '')
  const [baseCashFlow, setBaseCashFlow, setBaseCashFlowRaw] = useField(touched.current, 'baseCashFlow', INTAKE_DEFAULTS.baseCashFlow)
  // VAL-INCOME-BASIS-FIX-1: annual cost to hire a market-rate replacement to
  // run the business. Required by the API (400) below the signed SDE/EBITDA
  // crossover, since SDE carries the owner's full pay; blank is allowed here
  // and produces that 400 on run, not a client-side block.
  const [replacementCompensation, setReplacementCompensation, setReplacementCompensationRaw] = useField(touched.current, 'replacementCompensation', INTAKE_DEFAULTS.replacementCompensation)
  const [growthRate, setGrowthRate, setGrowthRateRaw] = useField(touched.current, 'growthRate', INTAKE_DEFAULTS.growthRate)
  const [projectionYears, setProjectionYears, setProjectionYearsRaw] = useField(touched.current, 'projectionYears', INTAKE_DEFAULTS.projectionYears)
  const [capWeight, setCapWeight, setCapWeightRaw] = useField(touched.current, 'capWeight', INTAKE_DEFAULTS.capWeight)
  // Market leg
  const [compMultiples, setCompMultiples, setCompMultiplesRaw] = useField(touched.current, 'compMultiples', '')
  // Asset floor
  const [totalAssets, setTotalAssets, setTotalAssetsRaw] = useField(touched.current, 'totalAssets', '')
  const [totalLiabilities, setTotalLiabilities, setTotalLiabilitiesRaw] = useField(touched.current, 'totalLiabilities', '')
  const [fairValueAdjustments, setFairValueAdjustments, setFairValueAdjustmentsRaw] = useField(touched.current, 'fairValueAdjustments', '')
  // Signed-methodology sections. `driverAnswers` is `question_id -> option
  // value`; there is no driver-score state on this screen any more, because a
  // score the browser holds is a score the browser can set (guardrail 1).
  const [driverAnswers, setDriverAnswersRaw] = useState<Record<string, string>>({})
  const setDriverAnswers = useCallback(
    (next: SetStateAction<Record<string, string>>) =>
      setDriverAnswersRaw(prev => {
        const n = typeof next === 'function' ? next(prev) : next
        for (const k of Object.keys(n)) if (n[k] !== prev[k]) touched.current.add(`answer:${k}`)
        return n
      }),
    [],
  )
  // VAL-INPUT-PERSIST-1D — the multiple override, moved here from the
  // deleted `OverrideSection`; round-trips through the draft like every
  // other Intake field, unlike the old box (which the `/run` path alone read).
  const [overrideMultiple, setOverrideMultiple, setOverrideMultipleRaw] = useField(touched.current, 'overrideMultiple', '')
  const [overrideRationale, setOverrideRationale, setOverrideRationaleRaw] = useField(touched.current, 'overrideRationale', '')
  const [overrideDisposition, setOverrideDisposition, setOverrideDispositionRaw] = useField(touched.current, 'overrideDisposition', '')

  const [hydrated, setHydrated] = useState(false)
  // True when the user typed before the draft arrived: those values are unsaved
  // edits, so autosave must not take the post-hydrate payload as its baseline.
  const [typedBeforeHydrate, setTypedBeforeHydrate] = useState(false)

  // VAL-INTAKE-HYDRATE-ALL-1 — the 10 build-up/DCF fields `saveDraft` sends
  // (`intakeDraftPatch`), restored by the hydration effect and reset by
  // `startClean()` from this ONE list — no ten copy-pasted lines either place.
  const hydrateFields: Array<[HydrateKey, (v: string) => void, string, string]> = [
    ['risk_free_rate', setRiskFreeRaw, 'riskFree', INTAKE_DEFAULTS.riskFree],
    ['company_specific_risk', setCompanySpecificRiskRaw, 'companySpecificRisk', INTAKE_DEFAULTS.companySpecificRisk],
    ['long_term_growth_rate', setLongTermGrowthRaw, 'longTermGrowth', INTAKE_DEFAULTS.longTermGrowth],
    ['equity_risk_premium', setEquityRiskPremiumRaw, 'equityRiskPremium', INTAKE_DEFAULTS.equityRiskPremium],
    ['size_premium', setSizePremiumRaw, 'sizePremium', INTAKE_DEFAULTS.sizePremium],
    ['growth_rate', setGrowthRateRaw, 'growthRate', INTAKE_DEFAULTS.growthRate],
    ['projection_years', setProjectionYearsRaw, 'projectionYears', INTAKE_DEFAULTS.projectionYears],
    ['capitalized_earnings_weight', setCapWeightRaw, 'capWeight', INTAKE_DEFAULTS.capWeight],
    ['base_cash_flow', setBaseCashFlowRaw, 'baseCashFlow', INTAKE_DEFAULTS.baseCashFlow],
    ['replacement_compensation', setReplacementCompensationRaw, 'replacementCompensation', INTAKE_DEFAULTS.replacementCompensation],
  ]

  // FIX_VAL-OWNER-INPUTS-2 — once the draft has settled AND the defaults have
  // arrived, fill each field the draft left empty and the user has not typed in.
  // The setters are the screen's own write, so autosave then saves the value
  // (attributed); a draft value or a typed value always wins.
  const startingApplied = useRef(false)
  useEffect(() => {
    if (!hydrated || !starting || startingApplied.current) return
    startingApplied.current = true
    for (const [draftKey, setter, name] of hydrateFields) {
      const d = starting[draftKey]
      const saved = draft ? draft[draftKey] : null
      if (d && (saved === null || saved === undefined) && !touched.current.has(name)) setter(String(d.value))
    }
  }, [hydrated, starting]) // eslint-disable-line react-hooks/exhaustive-deps

  // D3 — hydrate once the draft has settled (loaded or confirmed absent).
  // Only the fields this screen owns (asset floor) plus the shared driver
  // answers (D4) — never `benchmark_key`, which this screen has no field for.
  useEffect(() => {
    if (hydrated || draftLoading) return
    // R3.10 — normalized_earnings (Earnings card) hydrates too, or a
    // resumed draft shows this screen's blank field despite having one.
    if (draft) {
      // A field the user has typed in is never replaced by the draft.
      const open = (name: string) => !touched.current.has(name)
      if (draft.normalized_earnings && open('normalizedEarnings')) setNormalizedEarningsRaw(String(draft.normalized_earnings))
      if (draft.total_assets !== null && open('totalAssets')) setTotalAssetsRaw(String(draft.total_assets))
      if (draft.total_liabilities !== null && open('totalLiabilities')) setTotalLiabilitiesRaw(String(draft.total_liabilities))
      if (draft.fair_value_adjustments && open('fairValueAdjustments')) setFairValueAdjustmentsRaw(String(draft.fair_value_adjustments))
      // VAL-INPUT-PERSIST-1B B5 — comps are keyed off `.length`, not presence,
      // because the codec omits the key entirely when empty rather than
      // sending `[]` (`finalize_codec.py:101-102`).
      if (draft.comp_multiples?.length && open('compMultiples')) setCompMultiplesRaw(draft.comp_multiples.join(', '))
      // Driver answers merge per question: the user's own answers win.
      if (Object.keys(draft.answers).length > 0) {
        setDriverAnswersRaw(prev => {
          const merged = { ...draft.answers }
          for (const k of Object.keys(prev)) if (touched.current.has(`answer:${k}`)) merged[k] = prev[k]
          return merged
        })
      }
      if (draft.override_multiple !== null && draft.override_multiple !== undefined && open('overrideMultiple')) {
        setOverrideMultipleRaw(String(draft.override_multiple))
      }
      if (draft.override_rationale && open('overrideRationale')) setOverrideRationaleRaw(draft.override_rationale)
      if (draft.override_disposition && open('overrideDisposition')) setOverrideDispositionRaw(draft.override_disposition)
      // VAL-INTAKE-HYDRATE-ALL-1 — the 10 build-up/DCF fields; a null/undefined
      // draft value keeps the useState default rather than overwriting it.
      for (const [draftKey, setter, name] of hydrateFields) {
        const value: DraftInputs[typeof draftKey] = draft[draftKey]
        if (value !== null && value !== undefined && open(name)) setter(String(value))
      }
    }
    setTypedBeforeHydrate(touched.current.size > 0)
    setHydrated(true)
  }, [draft, draftLoading, hydrated])

  /** D3 "start clean" — clears the resumed asset-floor/driver fields back to
   * blank; the draft row itself is untouched (Tuning's half must survive). */
  function startClean() {
    dismissDraft()
    touched.current.clear()
    setNormalizedEarningsRaw('')
    setTotalAssetsRaw('')
    setTotalLiabilitiesRaw('')
    setFairValueAdjustmentsRaw('')
    setCompMultiplesRaw('')
    setDriverAnswersRaw({})
    setOverrideMultipleRaw('')
    setOverrideRationaleRaw('')
    setOverrideDispositionRaw('')
    for (const [draftKey, setter, , def] of hydrateFields) setter(starting?.[draftKey] ? String(starting[draftKey].value) : def)
  }

  return {
    riskFree, setRiskFree,
    equityRiskPremium, setEquityRiskPremium,
    sizePremium, setSizePremium,
    companySpecificRisk, setCompanySpecificRisk,
    longTermGrowth, setLongTermGrowth,
    normalizedEarnings, setNormalizedEarnings,
    baseCashFlow, setBaseCashFlow,
    replacementCompensation, setReplacementCompensation,
    growthRate, setGrowthRate,
    projectionYears, setProjectionYears,
    capWeight, setCapWeight,
    compMultiples, setCompMultiples,
    totalAssets, setTotalAssets,
    totalLiabilities, setTotalLiabilities,
    fairValueAdjustments, setFairValueAdjustments,
    driverAnswers, setDriverAnswers,
    overrideMultiple, setOverrideMultiple,
    overrideRationale, setOverrideRationale,
    overrideDisposition, setOverrideDisposition,
    hydrated,
    typedBeforeHydrate,
    startClean,
  }
}

/**
 * R6C — the `SaveDraftBody` ValuationIntake.tsx's pre-run `saveDraft` call
 * built inline (`handleRun`, lines 216-237 on main eae727b5). The ONE place
 * B and D add fields to.
 *
 * VAL-INTAKE-1 D2/D3/D4, corrected by VAL-NAV-STATE-1 R3 — the one working
 * draft, shared with Tuning. Intake used to save only once a draft already
 * existed, on the theory that it had no `benchmark_key` to originate the
 * row with — but a valuator who runs Intake before ever opening Tuning was
 * left with nothing saved at all, silently. Intake now originates the row
 * itself on its first save, bootstrapping `benchmark_key` from the
 * engagement's own signed industry profile (same map Tuning uses, R2) and
 * `normalized_earnings` from the field this screen already collects for
 * its own DCF leg — both real values, not placeholders that later need
 * correcting.
 *
 * VAL-INPUT-PERSIST-1B B4 — `comp_multiples` and `normalized_earnings` are
 * now sent unconditionally, not just on that first bootstrap save: today a
 * comps edit made after the draft already exists is never resent, so it
 * looks saved on screen and is gone on reload.
 *
 * VAL-INPUT-PERSIST-1D — the multiple override is sent unconditionally too,
 * same reasoning as comps/earnings above: a blank multiple means all three
 * override fields go as `null`, so clearing the control on screen actually
 * clears the draft rather than leaving the old values stranded server-side.
 */
export function intakeDraftPatch(
  body: RunBody,
  draft: DraftInputs | null,
  industryKey: string,
  overrides: { overrideMultiple: string; overrideRationale: string; overrideDisposition: string },
): SaveDraftBody {
  // A blank multiple means no override at all, whatever the reason/
  // disposition boxes still hold on screen — otherwise clearing the
  // multiple can leave a stale reason/disposition stranded server-side.
  const overrideMultiple = num(overrides.overrideMultiple)
  return {
    ...(draft
      ? {}
      : { benchmark_key: industryKey }),
    override_multiple: overrideMultiple,
    override_rationale: overrideMultiple === null ? null : (overrides.overrideRationale.trim() || null),
    override_disposition: overrideMultiple === null
      ? null
      : ((overrides.overrideDisposition || null) as OverrideDisposition | null),
    normalized_earnings: body.normalized_earnings,
    total_assets: body.total_assets,
    total_liabilities: body.total_liabilities,
    fair_value_adjustments: body.fair_value_adjustments,
    driver_answers: body.driver_answers ?? undefined,
    risk_free_rate: body.risk_free_rate,
    company_specific_risk: body.company_specific_risk,
    long_term_growth_rate: body.long_term_growth_rate,
    equity_risk_premium: body.equity_risk_premium,
    size_premium: body.size_premium,
    growth_rate: body.growth_rate,
    projection_years: body.projection_years,
    capitalized_earnings_weight: body.capitalized_earnings_weight,
    base_cash_flow: body.base_cash_flow,
    replacement_compensation: body.replacement_compensation,
    comp_multiples: body.comp_multiples,
  }
}
